import { useRef, type ReactNode } from 'react';
import {
  SLEEP_ZS,
  THINKING_DOT_SIZE,
  THINKING_DOT_STAGGER_MS,
  caughtUp,
  chapiPivots,
  chapiViewBox,
  helloWave,
  loading,
  popIn,
  sliceTrack,
  thinking,
} from '@chapsi/ui-tokens/motion';
import { ChapiArt, MarkArt } from './ChapiArt.js';
import { useMotion } from './hooks.js';
import { play } from './play.js';
import '../../styles/motion.css';

/**
 * Chapi's animated moments (docs/designs/motion/README.md). Each is decorative (aria-hidden), moves only
 * transform and opacity, and shows its still frame when the system asks for reduced motion
 * (`data-motion="still"`). Moments that start by themselves finish within five seconds.
 */

const vbCentre = (v: { x: number; y: number; width: number; height: number }) => ({ x: v.x + v.width / 2, y: v.y + v.height / 2 });

/** Hello wave: one wave and a blink (sign-in, onboarding, the greeting on My work). */
export function HelloChapi({ size, headset = false, className }: { size: number; headset?: boolean; className?: string }) {
  const armR = useRef<SVGGElement>(null);
  const eyes = useRef<SVGGElement>(null);
  const still = useMotion(
    () => [
      play(armR.current, helloWave.armR, { pivot: chapiPivots.armR }),
      play(eyes.current, helloWave.eyes, { pivot: chapiPivots.eyes }),
    ],
    [],
  );
  return (
    <span className={`cm-chapi ${className ?? ''}`} data-motion={still ? 'still' : 'play'} data-anim="hello" aria-hidden="true">
      <ChapiArt size={size} headset={headset} layers={{ armR, eyes }} />
    </span>
  );
}

/** Chapi pops in: up from below with an overshoot, then a wave (opening Chapi). */
export function PopChapi({ size, headset = false, className }: { size: number; headset?: boolean; className?: string }) {
  const root = useRef<SVGGElement>(null);
  const armR = useRef<SVGGElement>(null);
  const eyes = useRef<SVGGElement>(null);
  const vb = headset ? chapiViewBox.headset : chapiViewBox.plain;
  const still = useMotion(() => {
    const [from, to] = POP_SLICE;
    return [
      play(root.current, sliceTrack(popIn.root, from, to), { pivot: vbCentre(vb) }),
      play(armR.current, sliceTrack(popIn.armR, from, to), { pivot: chapiPivots.armR }),
      play(eyes.current, sliceTrack(popIn.eyes, from, to), { pivot: chapiPivots.eyes }),
    ];
  }, []);
  return (
    <span className={`cm-chapi ${className ?? ''}`} data-motion={still ? 'still' : 'play'} data-anim="pop" aria-hidden="true">
      <ChapiArt size={size} headset={headset} layers={{ root, armR, eyes }} />
    </span>
  );
}
/** From the pop until the second wave has settled. */
export const POP_SLICE = [0.18, 0.8] as const;

/** The greeting bubble that follows the pop (hiIn), starting as soon as Chapi has landed. */
export const POP_GREETING = { track: sliceTrack(popIn.hi, 0.4, 0.5), delay: 300 } as const;

/** The heading under a popping Chapi rises in just after Chapi lands (still with reduced motion). */
export function GreetingRise({ className, children }: { className?: string; children: ReactNode }) {
  const el = useRef<HTMLHeadingElement>(null);
  useMotion(() => [play(el.current, POP_GREETING.track, { delay: POP_GREETING.delay })], []);
  return <h2 ref={el} className={className}>{children}</h2>;
}

/** Thinking: head tilting, scratching, blinking, for as long as Chapi is working. */
export function ThinkingChapi({ size, className }: { size: number; className?: string }) {
  const head = useRef<SVGGElement>(null);
  const armR = useRef<SVGGElement>(null);
  const eyes = useRef<SVGGElement>(null);
  const still = useMotion(
    () => [
      play(head.current, thinking.head, { pivot: chapiPivots.head, iterations: Infinity }),
      play(armR.current, thinking.armR, { pivot: chapiPivots.armR, iterations: Infinity, alternate: true }),
      play(eyes.current, thinking.eyes, { pivot: chapiPivots.eyes, iterations: Infinity }),
    ],
    [],
  );
  return (
    <span className={`cm-chapi ${className ?? ''}`} data-motion={still ? 'still' : 'play'} data-anim="thinking" aria-hidden="true">
      <ChapiArt size={size} layers={{ head, armR, eyes }} />
    </span>
  );
}

/** The typing dots in Chapi's bubble: violet, purple, blue, each hopping in turn. */
export function ThinkingDots({ size = 7 }: { size?: number }) {
  const dots = [useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null)];
  const still = useMotion(
    () => dots.map((d, i) => play(d.current, thinking.dot, { delay: i * THINKING_DOT_STAGGER_MS, iterations: Infinity, scale: size / THINKING_DOT_SIZE })),
    [size],
  );
  return (
    <span className="cm-dots" data-motion={still ? 'still' : 'play'} data-anim="thinking-dots" aria-hidden="true" style={{ gap: Math.round(size * 0.7) }}>
      {dots.map((d, i) => <i key={i} ref={d} style={{ width: size, height: size }} />)}
    </span>
  );
}

/** All up to date: Chapi asleep, breathing, with z's drifting up (one pass, then still). */
export function SleepingChapi({ size, className }: { size: number; className?: string }) {
  const root = useRef<SVGGElement>(null);
  const zs = [useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null)];
  const vb = chapiViewBox.asleep;
  const scale = size / 150;
  const still = useMotion(
    () => [
      play(root.current, caughtUp.breathe, { pivot: { x: vb.x + vb.width / 2, y: vb.y + vb.height } }),
      ...zs.map((z, i) => play(z.current, caughtUp.z, { delay: SLEEP_ZS[i]!.delayMs, scale })),
    ],
    [scale],
  );
  return (
    <span className={`cm-chapi cm-sleep ${className ?? ''}`} data-motion={still ? 'still' : 'play'} data-anim="caught-up" aria-hidden="true">
      <ChapiArt size={size} pose="asleep" layers={{ root }} />
      {SLEEP_ZS.map((z, i) => (
        <i key={i} ref={zs[i]} className="cm-z" style={{ left: `${z.left * 100}%`, top: `${z.top * 100}%`, fontSize: Math.round(z.size * scale) }}>
          {z.letter}
        </i>
      ))}
    </span>
  );
}

/** Loading: the Chapsi mark turning while its leaves part and regroup. Only while something loads. */
export function MarkLoader({ size = 36, label = 'Loading' }: { size?: number; label?: string }) {
  const spin = useRef<HTMLSpanElement>(null);
  const leafA = useRef<SVGPathElement>(null);
  const leafB = useRef<SVGPathElement>(null);
  const still = useMotion(
    () => [
      play(spin.current, loading.spin, { iterations: Infinity }),
      play(leafA.current, loading.leafA, { pivot: { x: 178.5, y: 114 }, iterations: Infinity }),
      play(leafB.current, loading.leafB, { pivot: { x: 178.5, y: 114 }, iterations: Infinity }),
    ],
    [],
  );
  return (
    <div className="cm-loader" role="status" aria-busy="true" aria-label={label} data-motion={still ? 'still' : 'play'} data-anim="loading">
      <span ref={spin} className="cm-loader-mark" aria-hidden="true">
        <MarkArt size={size} leafA={leafA} leafB={leafB} />
      </span>
    </div>
  );
}
